import * as React from 'react';
import { css } from '@emotion/react';
import { Draft, Inline, Link, Text, Stack, useBreakpoint } from '@resi-media/resi-ui';
import { useAtomValue, useSetAtom } from 'jotai';
import { useLocation } from 'react-router';
import { localStoragePrefix } from '@studio/constants/env-variables';
import UrlPaths from '@studio/constants/url-paths';
import { usePrefix } from '@studio/hooks';
import { browserStateAtom } from '@studio/storeV2/browser';

const UnsupportedBrowserBanner = () => {
  const location = useLocation();
  const { commonT, linkT, warningT } = usePrefix('UnsupportedBrowserBanner');
  const mediaQuery = useBreakpoint();
  const { isBrowserSupported, isUnsupportedBannerVisible } = useAtomValue(browserStateAtom);
  const setBrowserState = useSetAtom(browserStateAtom);
  const ResponsiveContainer = mediaQuery.lg ? Inline : Stack;
  const isSoftwareEncoderPath = (
    [
      UrlPaths.UPLOADER.ROOT,
      UrlPaths.UPLOADER.RESET_PASSWORD,
      UrlPaths.UPLOADER.SUCCESS,
      UrlPaths.UNREGISTER.ROOT,
      UrlPaths.UNREGISTER.SUCCESS,
    ] as string[]
  ).includes(location.pathname);

  React.useEffect(() => {
    if (isBrowserSupported) {
      setBrowserState((prevState) => ({
        ...prevState,
        isUnsupportedBannerVisible: false,
      }));
    }
  }, [isBrowserSupported, setBrowserState]);

  return (
    !isSoftwareEncoderPath &&
    !isBrowserSupported &&
    isUnsupportedBannerVisible && (
      <Draft.AlertBanner
        css={css`
          position: fixed;
          bottom: 0;
          left: 0;
          width: 100%;
          z-index: 1000;
        `}
        dataTestId="unsupported-browser-banner"
        onClose={() => {
          setBrowserState((prevState) => ({
            ...prevState,
            isUnsupportedBannerVisible: false,
          }));
          localStorage.setItem(`${localStoragePrefix}-show-unsupported-browser-banner`, 'false');
        }}
        variant="warning"
      >
        <ResponsiveContainer dataTestId="unsupported-browser-banner__content">
          <Text>{warningT('unsupportedBrowser')}</Text>
          <Link href={linkT('whatIsStudio')} target="_blank">
            {commonT('learnMore')}
          </Link>
        </ResponsiveContainer>
      </Draft.AlertBanner>
    )
  );
};

UnsupportedBrowserBanner.displayName = 'UnsupportedBrowserBanner';

export default UnsupportedBrowserBanner;
